import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { WhatsAppInboxMessage } from '@zrsystem/contracts';
import { useCallback } from 'react';

import { listWhatsAppInboxMessages } from '@/features/notification/api/whatsapp-inbox.api.js';
import { resolveWhatsAppInboxMediaKind } from '@/features/notification/lib/whatsapp-inbox-media-kind.js';
import { notificationQueryKeys } from '@/features/notification/query-keys.js';

export const WHATSAPP_INBOX_MESSAGES_PAGE_SIZE = 50;

export function sortWhatsAppInboxMessagesAsc(
  messages: readonly WhatsAppInboxMessage[],
): WhatsAppInboxMessage[] {
  return [...messages].sort((left, right) => left.timestamp - right.timestamp);
}

const OPTIMISTIC_AUDIO_PREFIX = 'optimistic:';

export function isOptimisticWhatsAppInboxMessage(message: WhatsAppInboxMessage): boolean {
  return message.id.startsWith(OPTIMISTIC_AUDIO_PREFIX);
}

/** Local attachment shown before WAHA lists the sent message back. */
export function optimisticWhatsAppInboxMediaMessage(input: {
  readonly providerMessageId: string;
  readonly chatId: string;
  readonly file: File;
  readonly previewUrl?: string;
  readonly caption?: string;
  readonly contentType?: string;
}): WhatsAppInboxMessage {
  const previewUrl = input.previewUrl ?? URL.createObjectURL(input.file);
  const declared = input.contentType ?? input.file.type;
  const mimetype = declared.length > 0 ? declared : 'application/octet-stream';
  const caption = input.caption?.trim() ?? '';
  return {
    id: `${OPTIMISTIC_AUDIO_PREFIX}${input.providerMessageId}`,
    timestamp: Math.floor(Date.now() / 1000),
    from: input.chatId,
    fromMe: true,
    source: null,
    to: input.chatId,
    participant: null,
    body: caption.length > 0 ? caption : null,
    hasMedia: true,
    media: {
      url: previewUrl,
      mimetype,
      filename: input.file.name,
    },
    mediaUrl: previewUrl,
    ack: null,
    ackName: null,
    author: null,
    location: null,
    vCards: null,
    replyTo: null,
  };
}

/** Local voice note shown before WAHA lists the sent message back. */
export function optimisticWhatsAppInboxAudioMessage(input: {
  readonly providerMessageId: string;
  readonly chatId: string;
  readonly file: File;
}): WhatsAppInboxMessage {
  const contentType = input.file.type.length > 0 ? input.file.type : 'audio/webm';
  return optimisticWhatsAppInboxMediaMessage({ ...input, contentType });
}

function serverMediaReplacesOptimistic(
  server: WhatsAppInboxMessage,
  optimistic: WhatsAppInboxMessage,
): boolean {
  if (!server.fromMe || isOptimisticWhatsAppInboxMessage(server)) {
    return false;
  }
  const optimisticKind = resolveWhatsAppInboxMediaKind(optimistic);
  const serverKind = resolveWhatsAppInboxMediaKind(server);
  if (optimisticKind === null || optimisticKind !== serverKind) {
    return false;
  }
  return Math.abs(server.timestamp - optimistic.timestamp) <= 180;
}

function withoutSupersededOptimisticMedia(
  messages: readonly WhatsAppInboxMessage[],
): WhatsAppInboxMessage[] {
  const claimedServerIds = new Set<string>();
  const droppedIds = new Set<string>();
  for (const optimistic of messages) {
    if (!isOptimisticWhatsAppInboxMessage(optimistic)) {
      continue;
    }
    const match = messages.find(
      (server) =>
        !claimedServerIds.has(server.id) && serverMediaReplacesOptimistic(server, optimistic),
    );
    if (match === undefined) {
      continue;
    }
    claimedServerIds.add(match.id);
    droppedIds.add(optimistic.id);
  }
  for (const message of messages) {
    if (!droppedIds.has(message.id)) {
      continue;
    }
    const url = message.mediaUrl;
    if (url?.startsWith('blob:') === true) {
      URL.revokeObjectURL(url);
    }
  }
  return messages.filter((message) => !droppedIds.has(message.id));
}

export function mergeWhatsAppInboxMessages(
  existing: readonly WhatsAppInboxMessage[],
  incoming: readonly WhatsAppInboxMessage[],
): WhatsAppInboxMessage[] {
  const byId = new Map<string, WhatsAppInboxMessage>();
  for (const message of existing) {
    byId.set(message.id, message);
  }
  for (const message of incoming) {
    byId.set(message.id, message);
  }
  return withoutSupersededOptimisticMedia(sortWhatsAppInboxMessagesAsc([...byId.values()]));
}

export function appendOptimisticWhatsAppInboxMessage(
  queryClient: QueryClient,
  placeId: string,
  chatId: string,
  message: WhatsAppInboxMessage,
): void {
  queryClient.setQueryData(
    notificationQueryKeys.whatsappInboxMessages(placeId, chatId),
    (current: WhatsAppInboxMessage[] | undefined) =>
      mergeWhatsAppInboxMessages(current ?? [], [message]),
  );
}

export function useWhatsAppInboxMessages(placeId: string, chatId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: notificationQueryKeys.whatsappInboxMessages(placeId, chatId ?? ''),
    queryFn: async () => {
      if (chatId === null) {
        return [];
      }
      const response = await listWhatsAppInboxMessages(placeId, chatId, {
        limit: WHATSAPP_INBOX_MESSAGES_PAGE_SIZE,
        downloadMedia: true,
      });
      const fresh = sortWhatsAppInboxMessagesAsc(response.messages);
      const existing =
        queryClient.getQueryData<WhatsAppInboxMessage[]>(
          notificationQueryKeys.whatsappInboxMessages(placeId, chatId),
        ) ?? [];
      const pendingLocal = existing.filter((message) => isOptimisticWhatsAppInboxMessage(message));
      return mergeWhatsAppInboxMessages(pendingLocal, fresh);
    },
    enabled: placeId.length > 0 && chatId !== null,
  });

  const fetchMore = useCallback(async (): Promise<void> => {
    if (chatId === null || placeId.length === 0) {
      return;
    }

    const current = queryClient.getQueryData<WhatsAppInboxMessage[]>(
      notificationQueryKeys.whatsappInboxMessages(placeId, chatId),
    );
    if (current === undefined || current.length === 0) {
      return;
    }

    const oldestTimestamp = current[0]?.timestamp;
    if (oldestTimestamp === undefined) {
      return;
    }

    const response = await listWhatsAppInboxMessages(placeId, chatId, {
      afterTimestamp: oldestTimestamp,
      limit: WHATSAPP_INBOX_MESSAGES_PAGE_SIZE,
      downloadMedia: true,
    });

    if (response.messages.length === 0) {
      return;
    }

    queryClient.setQueryData(
      notificationQueryKeys.whatsappInboxMessages(placeId, chatId),
      mergeWhatsAppInboxMessages(current, response.messages),
    );
  }, [chatId, placeId, queryClient]);

  return {
    ...query,
    fetchMore,
  };
}
